import React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import { MerchProduct } from '@services/merch/types/api/merch-product';

import SimpleArtist from '@common/types/artist/simple-artist';

// Store
import AppState from '@webapp/store/state';
import {
  DeleteMerch,
  PublishProduct,
} from '@webapp/store/merch/action-creators';
import { getMainDomain } from '@webapp/utils/get-artist-site-url';

import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';
import SnackbarStatus from '@components/basics/snackbar/snackbar-status';

import MobileInfoRow from './mobile-row/info-row';
import DesktopInfoRow from './desktop-view/info-row';
import moment from 'moment';
import { SimpleRelease } from '@common/types/release/simple-release';

declare type MerchCatalogInfoRowProps = {
  user: any;
  allReleases: SimpleRelease[];
  merchProduct: MerchProduct;
  artist: SimpleArtist;
  isListOpen: boolean;
  isFilteredByAddedDate: boolean;
  openList: () => void;
};

interface StateProps {
  isMobile: boolean;
  baseUrl: string;
  handleCatalogMenu: (
    action: string,
    release: MerchProduct,
    baseUrl: string,
    artist: SimpleArtist,
    user: any,
    allReleases: SimpleRelease[],
    handleShowNotification: () => void
  ) => void;
}

const mapStateToProps = (state: AppState) => {
  const subdomain =
    state.subdomain.currentSubdomain?.subdomain ||
    state.artist.active?.shortId ||
    '';
  return {
    isMobile: state.ui.mobile,
    baseUrl: `https://${subdomain}.${getMainDomain()}/`,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  handleCatalogMenu: (
    action: string,
    release: MerchProduct,
    baseUrl: string,
    artist: SimpleArtist,
    user: any,
    allReleases: SimpleRelease[],
    handleShowNotification: () => void
  ) => {
    if (action === 'edit') {
      dispatch(push(`/merch/edit/${release.id}`));
      return;
    }
    if (['publish', 're-publish', 'publish-now'].includes(action)) {
      dispatch(new PublishProduct(release.artistId, release.id));
      return;
    }
    if (action === 'copy-link') {
      navigator.clipboard.writeText(baseUrl + 'merch/detail/' + release.id);
      handleShowNotification();
      return;
    }
    if (action === 'manage-ownership') {
      let merchRelease = allReleases.find(
        (releaseData) => releaseData.id === release.releaseId
      );
      dispatch(
        push(
          `/user-profile/change-split/merch/${release.releaseId}/${
            release.id
          }/${release.title}/${artist.type}/${
            artist?.ownerId !== user.id ? 'TRUE' : 'FALSE'
          }/${
            merchRelease?.releaseDate
              ? moment(moment(merchRelease.releaseDate).add(30, 'days')).diff(
                  moment()
                ) > 0
                ? 'FALSE'
                : 'TRUE'
              : 'FALSE'
          }`
        )
      );
    }
    if (
      ['cancel-publishing', 'takedown-product', 'delete-product'].includes(
        action
      )
    ) {
      dispatch(new DeleteMerch(release.artistId, release.id));
      return;
    }

    return;
  },
});

const CatalogInfoRow: React.FC<MerchCatalogInfoRowProps & StateProps> = ({
  user,
  allReleases,
  merchProduct,
  artist,
  isListOpen,
  openList,
  isMobile,
  baseUrl,
  isFilteredByAddedDate,
  handleCatalogMenu,
}) => {
  const [showNotification, setShowNotification] =
    React.useState<boolean>(false);
  const handleShowNotification = () => {
    setShowNotification(true);
  };
  return isMobile ? (
    <>
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status="success"
            title={<Translate id="ARTIST_SITE.COPIED" />}
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
      <MobileInfoRow
        item={merchProduct}
        allReleases={allReleases}
        user={user}
        artist={artist}
        isListOpen={isListOpen}
        openList={openList}
        baseUrl={baseUrl}
        handleShowNotification={handleShowNotification}
        handleCatalogMenu={handleCatalogMenu}
        isFilteredByAddedDate={isFilteredByAddedDate}
      />
    </>
  ) : (
    <>
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status="success"
            title={<Translate id="ARTIST_SITE.COPIED" />}
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
      <DesktopInfoRow
        allReleases={allReleases}
        item={{ ...merchProduct }}
        user={user}
        artist={artist}
        isListOpen={isListOpen}
        openList={openList}
        baseUrl={baseUrl}
        handleShowNotification={handleShowNotification}
        handleCatalogMenu={handleCatalogMenu}
      />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CatalogInfoRow);
